import { css } from "@emotion/react";
import { Box, Tab, Tabs, Theme, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { match } from "ts-pattern";

import { PAGE_TABS, PageTab } from "~/components/category/[id]/quest/type";
import CategoryFollowButton from "~/components/common/category/CategoryFollowButton";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import { useCategoryDetailContext } from "~/pages/category/[id]";

const TabSelectPanel = ({
  tabName,
  onClick,
}: {
  tabName: PageTab;
  onClick: () => void;
}) => {
  const label = match(tabName)
    .with("DATABASE", () => "Database")
    .with("QUEST", () => "Quest")
    .with("LIMITED", () => "Limited")
    .otherwise(() => {
      throw new Error("invalid tab name");
    });
  return (
    <Tab
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.code === "Enter" || event.code === "Space") {
          onClick();
        }
      }}
      tabIndex={0}
      label={label}
      /* Mui Tabs の value はこう見えて数値のみを取るため */
      value={PAGE_TABS.findIndex((v) => v === tabName)}
      sx={{
        textTransform: "none",
        fontWeight: "700",
        "&:hover": {
          textDecoration: "underline",
        },
      }}
    />
  );
};

const CategoryHeader = () => {
  const theme = useTheme();
  const {
    props: { category },
    selectedTab,
    setSelectedTab,
  } = useCategoryDetailContext();
  const router = useRouter();

  const handleTabChange = async (tabName: PageTab) => {
    setSelectedTab(tabName);
    await router.push(`/category/${category.id}?tab=${tabName}`);
  };

  return (
    <div css={rootStyle(theme)}>
      <div css={headerUpperStyle}>
        <h1 css={categoryNameStyle}>{category.name}</h1>
        <CategoryFollowButton categoryId={category.id} />
      </div>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        {/* Mui Tabs の value はこう見えて数値のみを取るため */}
        <Tabs value={PAGE_TABS.findIndex((v) => v === selectedTab)}>
          {PAGE_TABS.map((tabName) => (
            <TabSelectPanel
              key={tabName}
              tabName={tabName}
              onClick={() => handleTabChange(tabName)}
            />
          ))}
        </Tabs>
      </Box>
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  width: 100%;
  background: ${theme.palette.background.paper};
  border-top: 2px solid ${theme.palette.divider};
`;

const headerUpperStyle = css`
  padding-left: 2rem;
  margin-bottom: 2.5rem;

  ${mediaQueryIsMobile(css`
    padding-left: 1rem;
    margin-bottom: 1.5rem;
  `)};
`;

const categoryNameStyle = css`
  margin: 0;
  padding-top: 1.25rem;
  font-weight: 600;

  ${mediaQueryIsMobile(css`
    padding-bottom: 0.5rem;
    font-size: 2rem;
  `)};

  ${mediaQueryIsDesktop(css`
    padding-bottom: 1rem;
    font-size: 2.75rem;
  `)};
`;

export default CategoryHeader;
